import { useState, useEffect } from "react";

const FontLoader = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=Jost:wght@300;400;500;600&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    :root {
      --bone: #F5F0E8;
      --cream: #FAF7F2;
      --charcoal: #1C1C1C;
      --warm-black: #111010;
      --gold: #B89A6A;
      --gold-light: #D4B896;
      --muted: #7A7168;
      --border: rgba(184,154,106,0.25);
      --font-display: 'Cormorant Garamond', Georgia, serif;
      --font-body: 'Jost', sans-serif;
    }

    html { scroll-behavior: smooth; }

    body {
      background: var(--cream);
      color: var(--charcoal);
      font-family: var(--font-body);
      font-weight: 300;
      letter-spacing: 0.01em;
      overflow-x: hidden;
    }

    .reveal { opacity: 0; transform: translateY(32px); transition: opacity 0.85s cubic-bezier(.16,1,.3,1), transform 0.85s cubic-bezier(.16,1,.3,1); }
    .reveal.visible { opacity: 1; transform: none; }
    .reveal-left { opacity: 0; transform: translateX(-40px); transition: opacity 0.9s cubic-bezier(.16,1,.3,1), transform 0.9s cubic-bezier(.16,1,.3,1); }
    .reveal-left.visible { opacity: 1; transform: none; }
    .reveal-right { opacity: 0; transform: translateX(40px); transition: opacity 0.9s cubic-bezier(.16,1,.3,1), transform 0.9s cubic-bezier(.16,1,.3,1); }
    .reveal-right.visible { opacity: 1; transform: none; }
    .delay-1 { transition-delay: 0.12s !important; }
    .delay-2 { transition-delay: 0.24s !important; }
    .delay-3 { transition-delay: 0.36s !important; }
    .delay-4 { transition-delay: 0.48s !important; }

    .label { font-family: var(--font-body); font-weight: 500; font-size: 0.68rem; letter-spacing: 0.22em; text-transform: uppercase; color: var(--gold); }

    .btn-primary {
      display: inline-flex; align-items: center; gap: 10px;
      background: var(--gold); color: var(--warm-black);
      font-family: var(--font-body); font-weight: 500; font-size: 0.75rem;
      letter-spacing: 0.18em; text-transform: uppercase;
      padding: 16px 40px; border: none; cursor: pointer;
      transition: background 0.3s, transform 0.2s; text-decoration: none;
    }
    .btn-primary:hover { background: var(--gold-light); transform: translateY(-1px); }

    .btn-outline {
      display: inline-flex; align-items: center; gap: 10px;
      background: transparent; color: var(--bone);
      font-family: var(--font-body); font-weight: 500; font-size: 0.75rem;
      letter-spacing: 0.18em; text-transform: uppercase;
      padding: 15px 38px; border: 1px solid rgba(245,240,232,0.5); cursor: pointer;
      transition: border-color 0.3s, color 0.3s; text-decoration: none;
    }
    .btn-outline:hover { border-color: var(--gold); color: var(--gold); }

    .gold-rule { width: 40px; height: 1px; background: var(--gold); display: block; }

    /* NAV */
    nav {
      position: fixed; top: 0; left: 0; right: 0; z-index: 100;
      display: flex; align-items: center; justify-content: space-between;
      padding: 20px 48px;
      transition: background 0.4s, padding 0.4s;
      background: rgba(17,16,16,0.85);
      backdrop-filter: blur(12px);
      border-bottom: 1px solid var(--border);
    }
    nav.scrolled {
      background: rgba(17,16,16,0.93);
      backdrop-filter: blur(12px);
      padding: 14px 48px;
      border-bottom: 1px solid var(--border);
    }
    .nav-logo { font-family: var(--font-display); font-size: 1.35rem; color: var(--bone); letter-spacing: 0.04em; text-decoration: none; }
    .nav-logo span { color: var(--gold); font-style: italic; }
    .nav-links { display: flex; gap: 36px; align-items: center; }
    .nav-links a { font-size: 0.7rem; letter-spacing: 0.18em; text-transform: uppercase; color: rgba(245,240,232,0.75); text-decoration: none; transition: color 0.2s; }
    .nav-links a:hover { color: var(--gold); }
    .mobile-menu-btn { display: none; background: none; border: 1px solid var(--border); padding: 8px 12px; cursor: pointer; color: var(--bone); font-size: 0.7rem; letter-spacing: 0.12em; font-family: var(--font-body); text-decoration: none; }
    @media(max-width:768px){ .mobile-menu-btn { display: block; } .nav-links { display: none; } }

    /* HERO */
    .hero {
      min-height: 100vh; position: relative; overflow: hidden;
      display: flex; align-items: flex-end;
      background: var(--warm-black);
    }
    .hero-bg { position: absolute; inset: 0; background: linear-gradient(135deg,#0a0a0a 0%,#1a1510 40%,#0d0c0b 100%); }
    .hero-grid-overlay {
      position: absolute; inset: 0; opacity: 0.028;
      background-image: linear-gradient(var(--gold) 1px,transparent 1px), linear-gradient(90deg,var(--gold) 1px,transparent 1px);
      background-size: 80px 80px;
    }
    .hero-image-panel { position: absolute; right: 0; top: 0; bottom: 0; width: 52%; overflow: hidden; }
    .hero-image-panel::before {
      content: ''; position: absolute; inset: 0; z-index: 1;
      background: linear-gradient(to right, var(--warm-black) 0%, transparent 32%),
        linear-gradient(to top, var(--warm-black) 0%, transparent 28%);
    }
    .hero-image-panel img { width: 100%; height: 100%; object-fit: cover; object-position: center; display: block; }
    .hero-logo-overlay { position: absolute; bottom: 40px; right: 40px; z-index: 2; width: 180px; opacity: 0.85; }
    .hero-content { position: relative; z-index: 10; padding: 0 48px 80px; max-width: 620px; }
    .hero-eyebrow { margin-bottom: 28px; display: flex; align-items: center; gap: 16px; }
    .hero-h1 { font-family: var(--font-display); font-weight: 300; font-size: clamp(3.2rem,7vw,5.8rem); line-height: 1.02; color: var(--bone); margin-bottom: 28px; }
    .hero-h1 em { font-style: italic; color: var(--gold-light); display: block; }
    .hero-sub { font-size: 1rem; line-height: 1.75; color: rgba(245,240,232,0.65); max-width: 460px; margin-bottom: 48px; }
    .hero-cta-row { display: flex; align-items: center; gap: 28px; flex-wrap: wrap; }
    .hero-stats { display: flex; gap: 48px; margin-top: 80px; padding-top: 40px; border-top: 1px solid var(--border); }
    .hero-stat-num { font-family: var(--font-display); font-size: 2.4rem; color: var(--gold); line-height: 1; }
    .hero-stat-label { font-size: 0.68rem; letter-spacing: 0.14em; text-transform: uppercase; color: rgba(245,240,232,0.45); margin-top: 4px; }
    .scroll-hint { position: absolute; bottom: 32px; right: 48px; display: flex; flex-direction: column; align-items: center; gap: 10px; z-index: 10; }
    .scroll-line { width: 1px; height: 48px; background: linear-gradient(to bottom,var(--gold),transparent); animation: scrollPulse 2s ease-in-out infinite; }
    @keyframes scrollPulse { 0%,100%{opacity:0.4} 50%{opacity:1} }

    /* SECTIONS */
    .section { padding: 120px 0; }
    .section-inner { max-width: 1280px; margin: 0 auto; padding: 0 48px; }
    .section-dark { background: var(--warm-black); color: var(--bone); }
    .section-bone { background: var(--bone); }
    .section-cream { background: var(--cream); }
    .section-title { font-family: var(--font-display); font-size: clamp(2.4rem,4.5vw,3.8rem); font-weight: 300; line-height: 1.1; }
    .section-title em { font-style: italic; color: var(--gold); }
    .section-subtitle { font-size: 0.95rem; line-height: 1.8; color: var(--muted); max-width: 540px; margin-top: 20px; }

    /* PAIN */
    .pain-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 80px; align-items: center; }
    .pain-img-frame { width: 100%; aspect-ratio: 3/4; overflow: hidden; position: relative; }
    .pain-img-frame::after { content: ''; position: absolute; top: 24px; left: 24px; right: -24px; bottom: -24px; border: 1px solid var(--border); z-index: -1; pointer-events: none; }
    .pain-img-frame img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .pain-points { margin-top: 48px; display: flex; flex-direction: column; gap: 28px; }
    .pain-point { display: flex; gap: 20px; align-items: flex-start; }
    .pain-icon { width: 36px; height: 36px; flex-shrink: 0; border: 1px solid var(--border); display: flex; align-items: center; justify-content: center; color: var(--gold); }
    .pain-point-text h4 { font-family: var(--font-display); font-size: 1.2rem; font-weight: 400; margin-bottom: 6px; color: var(--bone); }
    .pain-point-text p { font-size: 0.9rem; line-height: 1.7; color: rgba(245,240,232,0.55); }

    /* BENEFITS */
    .benefits-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 1px; background: var(--border); margin-top: 80px; }
    .benefit-card { background: var(--cream); padding: 52px 40px; position: relative; overflow: hidden; }
    .benefit-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px; background: transparent; transition: background 0.3s; }
    .benefit-card:hover::before { background: var(--gold); }
    .benefit-num { font-family: var(--font-display); font-size: 4rem; color: rgba(184,154,106,0.12); line-height: 1; margin-bottom: 16px; }
    .benefit-title { font-family: var(--font-display); font-size: 1.4rem; font-weight: 400; margin-bottom: 16px; }
    .benefit-text { font-size: 0.88rem; line-height: 1.8; color: var(--muted); }

    /* GALLERY */
    .gallery-section { background: var(--warm-black); }
    .gallery-tabs { display: flex; border-bottom: 1px solid var(--border); margin-bottom: 60px; }
    .gallery-tab { font-size: 0.72rem; letter-spacing: 0.2em; text-transform: uppercase; padding: 16px 32px; cursor: pointer; border: none; background: transparent; color: rgba(245,240,232,0.4); border-bottom: 2px solid transparent; margin-bottom: -1px; transition: color 0.2s, border-color 0.2s; font-family: var(--font-body); }
    .gallery-tab.active { color: var(--gold); border-bottom-color: var(--gold); }
    .gallery-grid { display: grid; gap: 3px; }
    .gallery-grid.kitchen  { grid-template-columns: 2fr 1fr 1fr; grid-template-rows: 380px 280px; }
    .gallery-grid.bathroom { grid-template-columns: repeat(4, 1fr); grid-template-rows: 300px 300px; }
    .gallery-grid.basement { grid-template-columns: repeat(3, 1fr); grid-template-rows: 320px 320px; }
    .gallery-item { overflow: hidden; position: relative; cursor: pointer; }
    .gallery-item.tall { grid-row: span 2; }
    .gallery-item img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform 0.65s cubic-bezier(.16,1,.3,1); }
    .gallery-item:hover img { transform: scale(1.06); }
    .gallery-overlay { position: absolute; inset: 0; background: linear-gradient(to top,rgba(17,16,16,0.85) 0%,transparent 55%); opacity: 0; transition: opacity 0.3s; display: flex; align-items: flex-end; padding: 24px; }
    .gallery-item:hover .gallery-overlay { opacity: 1; }
    .gallery-overlay-text { font-family: var(--font-display); font-size: 1.1rem; color: var(--bone); font-style: italic; }

    /* PROCESS */
    .process-steps { display: grid; grid-template-columns: repeat(4,1fr); gap: 48px; margin-top: 72px; position: relative; }
    .process-steps::before { content: ''; position: absolute; top: 20px; left: 10%; right: 10%; height: 1px; background: linear-gradient(to right,transparent,var(--border),var(--border),transparent); }
    .process-step { text-align: center; }
    .step-circle { width: 40px; height: 40px; border: 1px solid var(--gold); border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 24px; font-family: var(--font-display); font-size: 0.95rem; color: var(--gold); background: var(--bone); position: relative; z-index: 1; }
    .step-title { font-family: var(--font-display); font-size: 1.25rem; margin-bottom: 12px; }
    .step-text { font-size: 0.85rem; line-height: 1.75; color: var(--muted); }

    /* TESTIMONIALS */
    .testimonials-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 2px; margin-top: 72px; }
    .testimonial-card { background: var(--bone); padding: 52px 48px; }
    .testimonial-card.featured { background: var(--charcoal); color: var(--bone); grid-column: 1/3; display: grid; grid-template-columns: 1fr 1fr; gap: 60px; align-items: center; }
    .quote-mark { font-family: var(--font-display); font-size: 5rem; line-height: 1; color: var(--gold); opacity: 0.4; margin-bottom: -16px; display: block; }
    .testimonial-text { font-family: var(--font-display); font-size: 1.15rem; line-height: 1.7; margin-bottom: 28px; }
    .t-author { font-size: 0.72rem; letter-spacing: 0.18em; text-transform: uppercase; color: var(--gold); }
    .t-meta { font-size: 0.8rem; color: var(--muted); margin-top: 4px; }
    .stars { color: var(--gold); font-size: 0.75rem; letter-spacing: 3px; margin-bottom: 16px; }
    .featured-quote { font-family: var(--font-display); font-size: 1.8rem; line-height: 1.4; font-weight: 300; font-style: italic; color: var(--bone); }
    .testimonial-img { width: 100%; height: 380px; object-fit: cover; display: block; }

    /* ABOUT */
    .about-grid { display: grid; grid-template-columns: 5fr 4fr; gap: 100px; align-items: center; }
    .about-credentials { margin-top: 52px; display: grid; grid-template-columns: 1fr 1fr; gap: 28px; }
    .credential { border-left: 2px solid var(--gold); padding-left: 16px; }
    .credential-num { font-family: var(--font-display); font-size: 2.2rem; color: var(--charcoal); }
    .credential-label { font-size: 0.78rem; color: var(--muted); margin-top: 2px; }
    .about-img-wrap { position: relative; }
    .about-img-wrap img { width: 100%; aspect-ratio: 4/5; object-fit: cover; display: block; }
    .about-img-wrap::after { content: ''; position: absolute; bottom: -20px; right: -20px; width: 60%; height: 60%; border: 1px solid var(--border); pointer-events: none; }

    /* BEYOND */
    .beyond-intro { max-width: 640px; margin: 0 auto; text-align: center; margin-bottom: 72px; }
    .beyond-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 3px; }
    .beyond-item { position: relative; overflow: hidden; aspect-ratio: 4/3; cursor: pointer; }
    .beyond-item img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform 0.65s cubic-bezier(.16,1,.3,1); }
    .beyond-item:hover img { transform: scale(1.07); }
    .beyond-overlay { position: absolute; inset: 0; background: linear-gradient(to top,rgba(17,16,16,0.9) 0%,rgba(17,16,16,0.15) 60%,transparent 100%); display: flex; align-items: flex-end; padding: 32px 28px; }
    .beyond-title { font-family: var(--font-display); font-size: 1.3rem; color: var(--bone); margin-bottom: 6px; }
    .beyond-sub { font-size: 0.78rem; color: rgba(245,240,232,0.55); letter-spacing: 0.08em; }

    /* FINAL CTA */
    .final-cta { min-height: 70vh; display: flex; align-items: center; background: var(--warm-black); position: relative; overflow: hidden; }
    .cta-bg-img { position: absolute; inset: 0; }
    .cta-bg-img img { width: 100%; height: 100%; object-fit: cover; opacity: 0.16; display: block; }
    .cta-bg-img::after { content: ''; position: absolute; inset: 0; background: radial-gradient(ellipse 80% 60% at 50% 100%,rgba(184,154,106,0.1) 0%,transparent 70%); }
    .cta-h2 { font-family: var(--font-display); font-size: clamp(2.8rem,6vw,5rem); color: var(--bone); line-height: 1.08; margin-bottom: 28px; font-weight: 300; }
    .cta-h2 em { color: var(--gold-light); font-style: italic; }
    .cta-sub { font-size: 1rem; color: rgba(245,240,232,0.55); line-height: 1.75; margin-bottom: 52px; max-width: 480px; }

    /* FOOTER */
    footer { background: #0C0C0B; color: var(--bone); padding: 80px 0 40px; }
    .footer-inner { max-width: 1280px; margin: 0 auto; padding: 0 48px; }
    .footer-top { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; gap: 60px; margin-bottom: 60px; }
    .footer-logo { font-family: var(--font-display); font-size: 1.5rem; color: var(--bone); display: block; margin-bottom: 16px; }
    .footer-logo span { color: var(--gold); font-style: italic; }
    .footer-tagline { font-size: 0.85rem; line-height: 1.75; color: rgba(245,240,232,0.4); max-width: 280px; }
    .footer-col h5 { font-size: 0.65rem; letter-spacing: 0.22em; text-transform: uppercase; color: var(--gold); margin-bottom: 20px; }
    .footer-col a { display: block; font-size: 0.85rem; color: rgba(245,240,232,0.45); text-decoration: none; margin-bottom: 10px; transition: color 0.2s; }
    .footer-col a:hover { color: var(--bone); }
    .footer-col p { font-size: 0.85rem; color: rgba(245,240,232,0.45); line-height: 1.8; }
    .footer-bottom { padding-top: 32px; border-top: 1px solid rgba(245,240,232,0.08); display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 16px; }
    .footer-copy { font-size: 0.75rem; color: rgba(245,240,232,0.25); }
    .footer-badges { display: flex; gap: 16px; flex-wrap: wrap; }
    .badge { font-size: 0.65rem; letter-spacing: 0.12em; color: rgba(245,240,232,0.25); padding: 5px 12px; border: 1px solid rgba(245,240,232,0.1); }

    /* RESPONSIVE */
    @media (max-width: 1024px) {
      .hero-image-panel { width: 45%; }
      .pain-grid, .about-grid { grid-template-columns: 1fr; gap: 48px; }
      .benefits-grid { grid-template-columns: 1fr 1fr; }
      .process-steps { grid-template-columns: 1fr 1fr; }
      .process-steps::before { display: none; }
      .footer-top { grid-template-columns: 1fr 1fr; gap: 40px; }
      .testimonials-grid { grid-template-columns: 1fr; }
      .testimonial-card.featured { grid-column: 1; grid-template-columns: 1fr; }
    }
    @media (max-width: 768px) {
      nav { padding: 16px 24px; }
      nav.scrolled { padding: 12px 24px; }
      .hero-image-panel { display: none; }
      .hero-content { padding: 0 24px 80px; max-width: 100%; }
      .section-inner { padding: 0 24px; }
      .section { padding: 80px 0; }
      .benefits-grid { grid-template-columns: 1fr; }
      .process-steps { grid-template-columns: 1fr; }
      .gallery-grid.kitchen { grid-template-columns: 1fr 1fr; grid-template-rows: 260px 200px; }
      .gallery-item.tall { grid-row: span 1; }
      .gallery-grid.bathroom, .gallery-grid.basement { grid-template-columns: 1fr 1fr; grid-template-rows: auto; }
      .gallery-grid.bathroom .gallery-item, .gallery-grid.basement .gallery-item { min-height: 260px; }
      .beyond-grid { grid-template-columns: 1fr 1fr; }
      .footer-top { grid-template-columns: 1fr; gap: 32px; }
      .hero-stats { gap: 28px; flex-wrap: wrap; }
      .cta-h2 { font-size: 2.4rem; }
    }
    @media (max-width: 480px) {
      .beyond-grid { grid-template-columns: 1fr; }
      .gallery-grid.kitchen { grid-template-columns: 1fr; }
    }
  `}</style>
);

// ── LuxeCraft real project photos ─────────────────────────────────────────────
const IMG = {
  // Hero panel — real LuxeCraft kitchen (marble waterfall island)
  hero:             "/images/kitchen-marble-ideas-waterfall-island-counter.png",

  // Pain section — kept as before state reference
  painBefore:       "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=900&q=80&fit=crop&crop=center",

  // Kitchen gallery — 5 real LuxeCraft kitchens
  kitchen1:         "/images/Luxurykitchen1.png",
  kitchen2:         "/images/20200402_105128.jpg",
  kitchen3:         "/images/imagejpeg_0(5).jpg",
  kitchen4:         "/images/IMG_0954.jpg",
  kitchen5:         "/images/kitchen-marble-ideas-waterfall-island-counter.png",

  // Bathroom gallery — 8 real LuxeCraft bathrooms
  bath1:            "/images/Tublogo.png",
  bath2:            "/images/20250516_155610.jpg",
  bath3:            "/images/20251017_122155.jpg",
  bath4:            "/images/20240313_143047.jpg",
  bath5:            "/images/20240313_143101.jpg",
  bath6:            "/images/20220427_163554.jpg",
  bath7:            "/images/20210422_161513.jpg",
  bath9:            "/images/20201021_125351.jpg",

  // Basement / Other gallery — 6 real LuxeCraft projects
  base1:            "/images/IMG_4596.jpg",
  base2:            "/images/IMG_3222.jpg",
  base3:            "/images/IMG_8552.jpg",
  base4:            "/images/20250613_164531.jpg",
  base5:            "/images/20250523_143103.jpg",
  base6:            "/images/20240710_163452.jpg",

  // Testimonial — real LuxeCraft kitchen after state
  testimonialAfter: "/images/imagejpeg_0(5).jpg",

  // About — craftsman at work (kept stock)
  about:            "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=900&q=80&fit=crop&crop=center",

  // Beyond projects — real LuxeCraft photos where available
  egress:           "/images/20240710_163452.jpg",
  windows:          "/images/20240710_163452.jpg",
  additions:        "/images/20250613_164531.jpg",
  flooring:         "/images/IMG_4596.jpg",
  exterior:         "/images/20240710_163452.jpg",
  custom:           "/images/20250523_143103.jpg",

  // Final CTA full-bleed background — real LuxeCraft bathroom
  ctaBg:            "/images/20250516_155610.jpg",
};

// ── Scroll reveal hook ────────────────────────────────────────────────────────
function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal,.reveal-left,.reveal-right");
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

// ─────────────────────────────────────────────────────────────────────────────
// NAVBAR
// ─────────────────────────────────────────────────────────────────────────────
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return (
    <nav className={scrolled ? "scrolled" : ""}>
      <a href="#top" className="nav-logo">Luxe<span>craft</span></a>
      <div className="nav-links">
        <a href="#about">About</a>
        <a href="#gallery">Gallery</a>
        <a href="#process">Process</a>
        <a href="#testimonials">Reviews</a>
        <a href="#contact" className="btn-primary" style={{padding:"10px 24px"}}>Free Consultation</a>
      </div>
      <a href="#contact" className="mobile-menu-btn">Book Now</a>
    </nav>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HERO
// ─────────────────────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section className="hero" id="top">
      <div className="hero-bg" />
      <div className="hero-grid-overlay" />

      {/* Full-bleed photo panel — right half */}
      <div className="hero-image-panel">
        <img
          src={IMG.hero}
          alt="Stunning luxury kitchen with white cabinetry, marble island and brass pendant lights"
        />
        <img src="/images/Luxecraftlogo_transparent.png" alt="Luxe Craft Design & Build" className="hero-logo-overlay" />
      </div>

      <div className="hero-content">
        <div className="hero-eyebrow">
          <span className="gold-rule" />
          <span className="label">Luxury Home Transformations</span>
        </div>
        <h1 className="hero-h1">
          Your Home Deserves
          <em>Nothing Less</em>
          Than Extraordinary
        </h1>
        <p className="hero-sub">
          We design and build kitchens, bathrooms, and living spaces that elevate your home — and your life. Bespoke craftsmanship, meticulous detail, lasting value.
        </p>
        <div className="hero-cta-row">
          <a href="#contact" className="btn-primary">Book a Free Consultation <span>→</span></a>
          <a href="#gallery" className="btn-outline">View Our Work</a>
        </div>
        <div className="hero-stats">
          {[["250+","Projects Completed"],["11+","Years of Excellence"],["Client Satisfaction"]].map(([n,l])=>(
            <div key={l}>
              <div className="hero-stat-num">{n}</div>
              <div className="hero-stat-label">{l}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="scroll-hint">
        <span style={{fontSize:"0.6rem",letterSpacing:"0.18em",color:"rgba(245,240,232,0.3)",writingMode:"vertical-rl",textTransform:"uppercase"}}>Scroll</span>
        <div className="scroll-line" />
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PAIN
// ─────────────────────────────────────────────────────────────────────────────
function PainSection() {
  return (
    <section className="section section-dark" id="pain">
      <div className="section-inner">
        <div className="pain-grid">

          {/* Photo column */}
          <div className="reveal-left">
            <div className="pain-img-frame">
              <img
                src={IMG.painBefore}
                alt="Outdated bathroom before renovation — old fixtures, poor lighting, cramped layout"
              />
              {/* Caption overlay */}
              <div style={{position:"absolute",bottom:"32px",left:"32px",right:"32px",background:"rgba(17,16,16,0.78)",padding:"20px 24px",backdropFilter:"blur(8px)"}}>
                <div style={{fontSize:"0.65rem",letterSpacing:"0.22em",textTransform:"uppercase",color:"var(--gold)",marginBottom:"6px"}}>The Before</div>
                <div style={{fontFamily:"var(--font-display)",fontSize:"1rem",color:"var(--bone)",lineHeight:1.5}}>Outdated fixtures, cramped layouts, and spaces that no longer serve you.</div>
              </div>
            </div>
          </div>

          {/* Text column */}
          <div className="reveal-right" style={{padding:"40px 0"}}>
            <span className="label">The Problem</span>
            <div style={{height:"12px"}} />
            <h2 className="section-title">
              Living With a Space
              <em> That Doesn't Reflect You</em>
            </h2>
            <p style={{fontSize:"0.95rem",lineHeight:1.8,color:"rgba(245,240,232,0.55)",marginTop:"20px"}}>
              Your home should be a sanctuary — a reflection of who you are and the life you've built. But outdated kitchens and bathrooms silently drain your joy every single day.
            </p>
            <div className="pain-points">
              {[
                { icon:"⌛", title:"Lost Time & Frustration",   desc:"Cramped counters, poor storage, and dated layouts turn everyday tasks into daily frustrations." },
                { icon:"📉", title:"Diminished Home Value",     desc:"Outdated kitchens and bathrooms are the #1 reason homes sit on the market longer and sell for less." },
                { icon:"😔", title:"Spaces That No Longer Inspire", desc:"You deserve to love where you live. A kitchen or bathroom from 2005 simply isn't good enough anymore." },
              ].map((p,i) => (
                <div className="pain-point" key={i}>
                  <div className="pain-icon">{p.icon}</div>
                  <div className="pain-point-text">
                    <h4>{p.title}</h4>
                    <p>{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SOLUTION / BENEFITS
// ─────────────────────────────────────────────────────────────────────────────
function SolutionSection() {
  const benefits = [
    { num:"01", title:"Bespoke Design, Built Around You",    text:"Every project begins with understanding how you live. Your kitchen should work the way you cook. Your bathroom should feel like your private retreat. We design for your life — not a catalog." },
    { num:"02", title:"Uncompromising Craftsmanship",        text:"We partner with master craftspeople and source materials that last generations. The difference between good and extraordinary is in details most contractors never consider." },
    { num:"03", title:"Seamless, Stress-Free Process",       text:"Renovation shouldn't derail your life. Our dedicated project managers keep everything on time, on budget, and communicate with you at every step." },
    { num:"04", title:"Significant Return on Investment",    text:"A Luxecraft kitchen renovation returns 80–100% of its cost at resale. But more importantly, you'll enjoy it every single day you live there." },
    { num:"05", title:"Licensed, Insured, Guaranteed",       text:"Full licensing across all trades, comprehensive insurance, and a 5-year workmanship warranty. You're protected completely." },
    { num:"06", title:"Trusted by 150+ Families",            text:"For 11 years, homeowners across the region have trusted us with their most important investment. Our reputation is built one perfect project at a time." },
  ];
  return (
    <section className="section section-cream" id="solution">
      <div className="section-inner">
        <div style={{maxWidth:"640px"}} className="reveal">
          <span className="label">The Luxecraft Difference</span>
          <div style={{height:"16px"}} />
          <h2 className="section-title" style={{color:"var(--charcoal)"}}>Spaces That <em>Move You,</em><br />Built to Last</h2>
          <p className="section-subtitle">We don't just renovate rooms. We craft environments that change how you feel about your home — and yourself — every single day.</p>
        </div>
        <div className="benefits-grid">
          {benefits.map((b,i) => (
            <div className={`benefit-card reveal delay-${(i%3)+1}`} key={i}>
              <div className="benefit-num">{b.num}</div>
              <h3 className="benefit-title">{b.title}</h3>
              <p className="benefit-text">{b.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// GALLERY
// ─────────────────────────────────────────────────────────────────────────────
const GALLERY = {
  kitchen: [
    { src: IMG.kitchen1, tall: true,  label: "Luxury Open Concept" },
    { src: IMG.kitchen2, tall: false, label: "Two-Tone Blue & White" },
    { src: IMG.kitchen3, tall: false, label: "Classic White Shaker" },
    { src: IMG.kitchen4, tall: false, label: "Clean Minimal White" },
    { src: IMG.kitchen5, tall: false, label: "Marble Waterfall Island" },
  ],
  bathroom: [
    { src: IMG.bath1,    tall: false, label: "Freestanding Soaker Tub" },
    { src: IMG.bath2,    tall: false, label: "Garden View Spa Bath" },
    { src: IMG.bath3,    tall: false, label: "Double Vanity En-Suite" },
    { src: IMG.bath4,    tall: false, label: "Navy & Gold Vanity" },
    { src: IMG.bath5,    tall: false, label: "Dark Vanity Marble Floor" },
    { src: IMG.bath6,    tall: false, label: "Rustic Stone Feature Wall" },
    { src: IMG.bath7,    tall: false, label: "Glass Shower & Soaker" },
    { src: IMG.bath9,    tall: false, label: "Farmhouse Shiplap Bath" },
  ],
  basement: [
    { src: IMG.base1,    tall: false, label: "Finished Basement Suite" },
    { src: IMG.base2,    tall: false, label: "Custom Built-In Media Wall" },
    { src: IMG.base3,    tall: false, label: "Statement Staircase" },
    { src: IMG.base4,    tall: false, label: "Living Room & Fireplace" },
    { src: IMG.base5,    tall: false, label: "Custom Pantry Cabinetry" },
    { src: IMG.base6,    tall: false, label: "Exterior Door Replacement" },
  ],
};

function GallerySection() {
  const [tab, setTab] = useState("kitchen");
  return (
    <section className="section gallery-section" id="gallery">
      <div className="section-inner">
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:"40px",flexWrap:"wrap",gap:"24px"}}>
          <div className="reveal">
            <span className="label">Our Portfolio</span>
            <div style={{height:"16px"}} />
            <h2 className="section-title" style={{color:"var(--bone)"}}>Crafted with <em>Intention</em></h2>
          </div>
          <a href="#contact" className="btn-primary reveal">Start Your Project →</a>
        </div>
        <div className="gallery-tabs">
          {[["kitchen","Kitchens"],["bathroom","Bathrooms"],["basement","Basements"]].map(([k,l]) => (
            <button key={k} className={`gallery-tab${tab===k?" active":""}`} onClick={() => setTab(k)}>{l}</button>
          ))}
        </div>
        <div className={`gallery-grid ${tab}`}>
          {GALLERY[tab].map((item,i) => (
            <div key={`${tab}-${i}`} className={`gallery-item${item.tall?" tall":""}`}>
              <img src={item.src} alt={item.label} loading="lazy" />
              <div className="gallery-overlay">
                <span className="gallery-overlay-text">{item.label}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PROCESS
// ─────────────────────────────────────────────────────────────────────────────
function ProcessSection() {
  const steps = [
    { num:"1", title:"Discovery Call",    text:"We learn about your vision, budget, lifestyle, and timeline. No pressure — just an honest conversation about what's possible." },
    { num:"2", title:"Design & Planning", text:"Our project manager will review the select materials and design. Our goal is top-quality finish in the exact time-line given! Much different than most construction companies"},
    { num:"3", title:"Expert Build",      text:"Our own tradespeople-execute the build with precision. Your dedicated project manager keeps you informed daily." },
    { num:"4", title:"Final Reveal",      text:"We do a thorough walkthrough together. You don't sign off until every detail meets your exacting standards." },
  ];
  return (
    <section className="section section-bone" id="process">
      <div className="section-inner">
        <div style={{textAlign:"center"}} className="reveal">
          <span className="label">How We Work</span>
          <div style={{height:"16px"}} />
          <h2 className="section-title" style={{color:"var(--charcoal)"}}>A Process as <em>Refined</em> as the Result</h2>
          <p style={{fontSize:"0.95rem",lineHeight:1.8,color:"var(--muted)",maxWidth:"500px",margin:"20px auto 0"}}>Renovation shouldn't be stressful. Our proven four-step process gives you complete clarity and confidence from first call to final reveal.</p>
        </div>
        <div className="process-steps">
          {steps.map((s,i) => (
            <div key={i} className={`process-step reveal delay-${i+1}`}>
              <div className="step-circle">{s.num}</div>
              <h3 className="step-title">{s.title}</h3>
              <p className="step-text">{s.text}</p>
            </div>
          ))}
        </div>
        <div style={{marginTop:"72px",background:"var(--charcoal)",padding:"48px 52px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:"32px",flexWrap:"wrap"}} className="reveal">
          <div>
            <div style={{fontFamily:"var(--font-display)",fontSize:"1.6rem",color:"var(--bone)",lineHeight:1.3}}>Ready to see what's possible <em style={{color:"var(--gold-light)"}}>in your home?</em></div>
            <p style={{fontSize:"0.88rem",color:"rgba(245,240,232,0.5)",marginTop:"10px"}}>Free consultations available 7 days a week. No obligation, ever.</p>
          </div>
          <a href="#contact" className="btn-primary">Book Your Free Consultation →</a>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TESTIMONIALS
// ─────────────────────────────────────────────────────────────────────────────
function TestimonialsSection() {
  const cards = [
    { quote:"Luxecraft transformed our outdated 1990s kitchen into something I genuinely wake up excited to use every morning. The attention to detail was extraordinary — they noticed things we hadn't even thought of.", author:"Margaret & David T.", meta:"Kitchen Renovation" },
    { quote:"Our master bathroom now feels like a five-star hotel suite. The project came in on time and exactly on budget. I've already referred three friends.", author:"Sandra R.", meta:"Spa Bathroom" },
    { quote:"After three bad experiences with other contractors, I was skeptical. The Luxecraft team was professional, communicative, and the finished basement exceeded every expectation.", author:"James & Carol M.", meta:"Full Basement" },
  ];
  return (
    <section className="section section-cream" id="testimonials">
      <div className="section-inner">
        <div className="reveal">
          <span className="label">Client Stories</span>
          <div style={{height:"16px"}} />
          <h2 className="section-title" style={{color:"var(--charcoal)"}}>Loved by the Homeowners <em>We Serve</em></h2>
        </div>
        <div className="testimonials-grid">
          {/* Featured card with photo */}
          <div className="testimonial-card featured reveal">
            <div>
              <div style={{display:"flex",gap:"4px",marginBottom:"24px"}}>
                {[1,2,3,4,5].map(i=><span key={i} style={{color:"var(--gold)",fontSize:"1.1rem"}}>★</span>)}
              </div>
              <p className="featured-quote">"We spent months agonizing over whether to move or renovate. Luxecraft made the decision easy — they showed us what our home could become, and delivered something even more stunning than we imagined."</p>
              <div style={{marginTop:"32px"}}>
                <div style={{fontSize:"0.72rem",letterSpacing:"0.18em",textTransform:"uppercase",color:"var(--gold)"}}>Patricia & Robert K.</div>
                <div style={{fontSize:"0.8rem",color:"rgba(245,240,232,0.4)",marginTop:"4px"}}>Complete Kitchen & Master Bath, — $185,000 project</div>
              </div>
            </div>
            {/* Stock photo of finished luxury kitchen */}
            <div style={{overflow:"hidden"}}>
              <img
                src={IMG.testimonialAfter}
                alt="Completed luxury kitchen renovation — bright white cabinetry, waterfall island, gold fixtures"
                className="testimonial-img"
              />
            </div>
          </div>

          {cards.map((c,i) => (
            <div key={i} className={`testimonial-card reveal delay-${i+1}`}>
              <div className="stars">★★★★★</div>
              <span className="quote-mark">"</span>
              <p className="testimonial-text">{c.quote}</p>
              <div className="t-author">{c.author}</div>
              <div className="t-meta">{c.meta}</div>
            </div>
          ))}
        </div>

        {/* Stats bar */}
        <div style={{display:"flex",justifyContent:"center",gap:"60px",marginTop:"72px",padding:"48px 0",borderTop:"1px solid var(--border)",flexWrap:"wrap"}} className="reveal">
          {[["150+","Projects Completed"],["Client Satisfaction"],["5-Year","Workmanship Warranty"],["11+","Years in Business"]].map(([n,l],i)=>(
            <div key={i} style={{textAlign:"center"}}>
              <div style={{fontFamily:"var(--font-display)",fontSize:"2.6rem",color:"var(--gold)",lineHeight:1}}>{n}</div>
              <div style={{fontSize:"0.7rem",letterSpacing:"0.14em",textTransform:"uppercase",color:"var(--muted)",marginTop:"6px"}}>{l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ABOUT
// ─────────────────────────────────────────────────────────────────────────────
function AboutSection() {
  return (
    <section className="section section-bone" id="about">
      <div className="section-inner">
        <div className="about-grid">
          <div className="reveal-left">
            <span className="label">About Luxecraft</span>
            <div style={{height:"20px"}} />
            <h2 className="section-title" style={{color:"var(--charcoal)"}}>Built on Integrity.<br /><em>Driven by Craft.</em></h2>
            <p style={{fontSize:"0.95rem",lineHeight:1.85,color:"var(--muted)",marginTop:"24px"}}>Founded in 2004 by master carpenter Michael Arms, Luxecraft Design & Build began with a simple belief: every homeowner deserves to love where they live. What started as a two-man kitchen renovation company has grown into a full-service design-build firm with 10 dedicated craftspeople.</p>
            <p style={{fontSize:"0.95rem",lineHeight:1.85,color:"var(--muted)",marginTop:"16px"}}>We've never compromised on materials, never cut a corner on craft, and never stopped learning. Our team trains continuously — attending trade shows and sourcing the finest materials from all over the world.</p>
            <div className="about-credentials">
              {[["$10M+","In completed projects"],["10+","Expert tradespeople"],["#1 Rated","In the region"],["Collaborate with Certified Designers"]].map(([n,l],i)=>(
                <div key={i} className="credential">
                  <div className="credential-num">{n}</div>
                  <div className="credential-label">{l}</div>
                </div>
              ))}
            </div>
          </div>
          {/* Portrait photo of craftsman */}
          <div className="about-img-wrap reveal-right">
            <img
              src={IMG.about}
              alt="Luxecraft master craftsman building custom cabinetry"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BEYOND KITCHENS
// ─────────────────────────────────────────────────────────────────────────────
const BEYOND = [
  { src: IMG.egress,    title:"Basement Egress",          sub:"Safe, code-compliant emergency access" },
  { src: IMG.windows,   title:"Window & Door Replacement", sub:"Energy efficiency meets curb appeal" },
  { src: IMG.additions, title:"Home Additions",            sub:"Expand your living space beautifully" },
  { src: IMG.flooring,  title:"Flooring & Tile",           sub:"Hardwood, engineered, ceramic & stone" },
  { src: IMG.exterior,  title:"Exterior Renovations",      sub:"First impressions that last" },
  { src: IMG.custom,    title:"Custom Carpentry",           sub:"Built-ins, millwork & unique solutions" },
];

function BeyondSection() {
  return (
    <section className="section section-dark" id="beyond">
      <div className="section-inner">
        <div className="beyond-intro reveal">
          <span className="label">Every Project, Every Scale</span>
          <div style={{height:"16px"}} />
          <h2 className="section-title" style={{color:"var(--bone)"}}>We Build <em>Everything</em><br />Your Home Needs</h2>
          <p style={{fontSize:"0.92rem",lineHeight:1.8,color:"rgba(245,240,232,0.5)",marginTop:"20px"}}>Beyond kitchens, bathrooms, and basements — we handle every project that makes your home more beautiful, functional, and valuable. One trusted partner for your entire home.</p>
        </div>
        <div className="beyond-grid">
          {BEYOND.map((p,i) => (
            <div key={i} className={`beyond-item reveal delay-${(i%3)+1}`}>
              <img src={p.src} alt={p.title} loading="lazy" />
              <div className="beyond-overlay">
                <div>
                  <div className="beyond-title">{p.title}</div>
                  <div className="beyond-sub">{p.sub}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CONTACT / FINAL CTA
// ─────────────────────────────────────────────────────────────────────────────
function ContactSection() {
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData).toString(),
      });
      setDone(true);
    } catch (err) {
      setDone(true);
    }
  };

  return (
    <section className="final-cta" id="contact">
      <div className="cta-bg-img">
        <img src={IMG.ctaBg} alt="Luxurious open-plan living and kitchen space" />
      </div>
      <div className="section-inner" style={{width:"100%",position:"relative",zIndex:1}}>
        {done ? (
          <div style={{textAlign:"center",color:"var(--bone)",maxWidth:"560px",margin:"0 auto"}}>
            <div style={{fontFamily:"var(--font-display)",fontSize:"1.4rem",color:"var(--gold)",marginBottom:"16px",fontStyle:"italic"}}>Thank You!</div>
            <h2 className="cta-h2">We'll Be in Touch <em>Within 24 Hours</em></h2>
            <p className="cta-sub">Your free consultation has been requested. Our design team will reach out shortly to schedule a time that works for you.</p>
          </div>
        ) : (
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"100px",alignItems:"center"}}>
            <div className="reveal-left">
              <span className="label" style={{color:"var(--gold)"}}>Free Consultation</span>
              <div style={{height:"20px"}} />
              <h2 className="cta-h2" style={{textAlign:"left"}}>Your Dream Space<br />Starts with a <em>Conversation</em></h2>
              <p style={{fontSize:"0.95rem",lineHeight:1.8,color:"rgba(245,240,232,0.5)",marginTop:"20px",marginBottom:"40px"}}>No pressure. No hard sell. Just an honest conversation about your home, your vision, and what's genuinely possible within your budget.</p>
              <div style={{display:"flex",flexDirection:"column",gap:"16px"}}>
                {["Free in-home design consultation","Detailed project scope & estimate","No commitment required"].map((item,i)=>(
                  <div key={i} style={{display:"flex",alignItems:"center",gap:"12px",fontSize:"0.88rem",color:"rgba(245,240,232,0.65)"}}>
                    <span style={{color:"var(--gold)"}}>✓</span> {item}
                  </div>
                ))}
              </div>
            </div>
            <div className="reveal-right">
              <div style={{background:"rgba(17,16,16,0.6)",border:"1px solid var(--border)",padding:"48px 40px",backdropFilter:"blur(16px)"}}>
                <div style={{fontFamily:"var(--font-display)",fontSize:"1.3rem",color:"var(--bone)",marginBottom:"32px"}}>Book Your Free Consultation</div>
                <form name="consultation" method="POST" data-netlify="true" netlify-honeypot="bot-field" onSubmit={handleSubmit}
                  style={{display:"flex",flexDirection:"column",gap:"16px"}}>
                  <input type="hidden" name="form-name" value="consultation" />
                  <input type="hidden" name="bot-field" />
                  <input type="text" name="name" placeholder="Your Full Name" required
                    style={{width:"100%",background:"rgba(245,240,232,0.07)",border:"1px solid rgba(184,154,106,0.22)",padding:"14px 18px",color:"var(--bone)",fontFamily:"var(--font-body)",fontSize:"0.88rem",outline:"none"}} />
                  <input type="email" name="email" placeholder="Email Address" required
                    style={{width:"100%",background:"rgba(245,240,232,0.07)",border:"1px solid rgba(184,154,106,0.22)",padding:"14px 18px",color:"var(--bone)",fontFamily:"var(--font-body)",fontSize:"0.88rem",outline:"none"}} />
                  <input type="tel" name="phone" placeholder="Phone Number"
                    style={{width:"100%",background:"rgba(245,240,232,0.07)",border:"1px solid rgba(184,154,106,0.22)",padding:"14px 18px",color:"var(--bone)",fontFamily:"var(--font-body)",fontSize:"0.88rem",outline:"none"}} />
                  <select name="project"
                    style={{width:"100%",background:"rgba(17,16,16,0.85)",border:"1px solid rgba(184,154,106,0.22)",padding:"14px 18px",color:"rgba(245,240,232,0.35)",fontFamily:"var(--font-body)",fontSize:"0.88rem",outline:"none",cursor:"pointer"}}>
                    <option value="" disabled selected>Project Type</option>
                    <option value="kitchen">Kitchen Renovation</option>
                    <option value="bathroom">Bathroom Renovation</option>
                    <option value="basement">Basement Finishing</option>
                    <option value="multiple">Multiple Rooms</option>
                    <option value="other">Other / Not Sure Yet</option>
                  </select>
                  <textarea name="message" placeholder="Tell us about your project (optional)" rows={3}
                    style={{width:"100%",background:"rgba(245,240,232,0.07)",border:"1px solid rgba(184,154,106,0.22)",padding:"14px 18px",color:"var(--bone)",fontFamily:"var(--font-body)",fontSize:"0.88rem",outline:"none",resize:"vertical"}} />
                  <button type="submit" className="btn-primary" style={{width:"100%",justifyContent:"center",padding:"18px"}}>
                    Request My Free Consultation
                  </button>
                  <p style={{fontSize:"0.72rem",color:"rgba(245,240,232,0.3)",textAlign:"center",letterSpacing:"0.06em"}}>We respond within 24 hours · No spam, ever</p>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FOOTER
// ─────────────────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer>
      <div className="footer-inner">
        <div className="footer-top">
          <div>
            <img src="/images/Luxecraftlogo.png" alt="Luxe Craft Design & Build" style={{height:"80px",width:"80px",objectFit:"contain",marginBottom:"16px",display:"block"}} />
            <p className="footer-tagline">Design & Build specialists transforming kitchens, bathrooms, and living spaces across the region since 2015.</p>
            <div style={{marginTop:"28px",display:"flex",gap:"12px",flexWrap:"wrap"}}>
              {["Houzz","Google","BBB"].map(b=>(
                <div key={b} style={{padding:"6px 14px",border:"1px solid rgba(245,240,232,0.1)",fontSize:"0.68rem",letterSpacing:"0.12em",color:"rgba(245,240,232,0.3)"}}>{b} ★ 4.9</div>
              ))}
            </div>
          </div>
          <div className="footer-col">
            <h5>Services</h5>
            {["Kitchen Renovations","Bathroom Renovations","Basement Finishing","Home Additions","Custom Carpentry","Flooring & Tile","Egress Windows","Exterior Work"].map(s=>(
              <a key={s} href="#gallery">{s}</a>
            ))}
          </div>
          <div className="footer-col">
            <h5>Company</h5>
            {["About Us","Our Process","Portfolio","Testimonials","Careers","Blog"].map(s=>(
              <a key={s} href="#about">{s}</a>
            ))}
          </div>
          <div className="footer-col">
            <h5>Contact</h5>
            <p> PO Box 420 <br />Harleysville, PA 19438<br /><br /><strong style={{color:"var(--gold)"}}>267-405-1561</strong><br /><br />www.luxecraftdesignbuild@gmail.com<br /><br />Mon–Fri: 8am – 6pm<br />Sat: 9am – 2pm</p>
          </div>
        </div>
        <div className="footer-bottom">
          <span className="footer-copy">© 2024 Luxecraft Design & Build Inc. All rights reserved.</span>
          <div className="footer-badges">
            <span className="badge">Licensed & Insured</span>
            <span className="badge">NKBA Member</span>
            <span className="badge">BBB Accredited</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// APP
// ─────────────────────────────────────────────────────────────────────────────
export default function LuxecraftWebsite() {
  useScrollReveal();
  return (
    <>
      <FontLoader />
      <Navbar />
      <Hero />
      <PainSection />
      <SolutionSection />
      <GallerySection />
      <ProcessSection />
      <TestimonialsSection />
      <AboutSection />
      <BeyondSection />
      <ContactSection />
      <Footer />
    </>
  );
}
